import { MessageInstance } from 'antd/es/message/interface'
import { FormFieldsByUniqueID } from 'components/form/formTypes'
import { useRolodexEdit } from 'features/fde/rolodexAPI'
import {
  GroupingInfoData,
  GroupingInfoFormFields,
} from 'features/groupings/groupingInfoFormFields'
import { useMemo, useRef, useState } from 'react'
import {
  DropdownOption,
  GeckoSite,
  GeckoSiteGrouping,
  RolodexEntryPatchJson,
} from 'types'

import { groupingsToFlatArray } from './GroupingsTableColumns'

export const useGroupings = (
  site: GeckoSite | undefined,
  onGroupingCreated?: () => void
) => {
  const [isGroupingInfoModalOpen, setIsGroupingInfoModalOpen] = useState(false)
  const [groupingID, setGroupingID] = useState<string | undefined>(undefined)
  const [groupingData, setGroupingData] = useState<GroupingInfoData>({
    name: '',
    category: undefined,
    description: '',
    parent: undefined,
  })
  const groupingFormRef = useRef<HTMLFormElement>(null)
  const { rolodexPatch } = useRolodexEdit()

  const existingGroupings = useMemo(() => {
    return groupingsToFlatArray(site?.data.groupings || {})
  }, [site])

  const parentGroupingOptions: DropdownOption[] = useMemo(() => {
    return existingGroupings
      .filter((grouping: GeckoSiteGrouping) => grouping.id !== groupingID)
      .map((grouping: GeckoSiteGrouping) => ({
        value: grouping.id,
        label: grouping.name,
      }))
  }, [existingGroupings, groupingID])

  const groupingInfoFormFields: FormFieldsByUniqueID[] = useMemo(
    () => [
      {
        uniqueID: 'groupingInfoForm',
        formFields: GroupingInfoFormFields({
          data: groupingData,
          parentGroupingOptions,
        }),
      },
    ],
    [groupingData, parentGroupingOptions]
  )

  const handleOpenGroupingInfoModal = () => {
    groupingFormRef.current?.reset()
    setIsGroupingInfoModalOpen(true)
  }

  const handleEditGroupingInfo = (grouping: GeckoSiteGrouping) => {
    setGroupingID(grouping.id)
    setGroupingData(grouping)
    handleOpenGroupingInfoModal()
  }

  const handleCloseGroupingInfoModal = () => {
    setGroupingData({
      name: '',
      category: undefined,
      description: '',
      parent: undefined,
    })
    setGroupingID(undefined)
    setIsGroupingInfoModalOpen(false)
  }

  const handleCancelGroupingInfo = () => {
    handleCloseGroupingInfoModal()
  }

  const handleSaveGroupingInfo = () => {
    if (groupingFormRef.current) {
      const event = new Event('submit', { bubbles: true, cancelable: true })
      groupingFormRef.current.dispatchEvent(event)
    }
  }

  const handlePatchToRolodex = (
    entry: RolodexEntryPatchJson<
      Partial<GeckoSite['data']>,
      Partial<GeckoSite['tags']>
    >,
    messageApi?: MessageInstance
  ) => {
    return rolodexPatch([entry])
      .then(() => {
        messageApi?.success('Grouping saved successfully!')
        handleCloseGroupingInfoModal()
        onGroupingCreated?.()
      })
      .catch((error) => {
        messageApi?.error('Failed to save grouping')
        throw error
      })
  }

  return {
    isGroupingInfoModalOpen,
    groupingFormRef,
    groupingID,
    setGroupingID,
    groupingData,
    setGroupingData,
    existingGroupings,
    groupingInfoFormFields,
    handleOpenGroupingInfoModal,
    handleEditGroupingInfo,
    handleCloseGroupingInfoModal,
    handleCancelGroupingInfo,
    handleSaveGroupingInfo,
    handlePatchToRolodex,
  }
}
